'use strict';

import fs from 'fs';
import Path from 'path';
import log from 'fancy-log';
import pluginError from 'plugin-error';
import through2 from 'through2';
import Vinyl from 'vinyl';
import * as util from '../utilities';

const PLUGIN_NAME = 'gulp-ps-manifest';

//
// (Options):
//
// name of manifest file.
//  manifest: string; default is 'src/manifest.json'
//
// name of powershell module.
//  powerShellModuleName: string; (required)
//
// flag of comment removable.
//  removeComments: boolean; default is true.
//
// prefix name to be used to remove ambiguity in JEA.
//  prefixName: string; (optional)
//
export interface Options {
    manifest: string;
    powerShellModuleName: string;
    removeComments: boolean;
    prefixName?: string;
}

/**
 * Plugin level function
 */
function gulpPsManifest(options: Options) {
    // override options settings if not specified.
    options = Object.assign(
        <Options>{
            manifest: 'src/manifest.json',
            removeComments: true
        },
        options || {});

    function findCommand(data, result) {
        if (typeof data === 'object') {
            if (data.command) {
                result.push(data);
                return;
            }

            for (const key in data) {
                if (data.hasOwnProperty(key)) {
                    const next = data[key];
                    findCommand(next, result);
                }
            }
        }
    }

    function convert(command: string, file: string, content: string): string {
        const commentStart = '<#';
        const commentEnd = '#>';
        const comment = '#';
        let script = '##' + command + '##:' + file + '\n';
        let skipping = false;
        const lines = content.split('\n');
        lines.forEach(value => {
            let text = value.replace('\r', '');
            if (options.removeComments) {
                let process = true;
                text = text.trim();
                if (text.startsWith(commentStart)) {
                    skipping = true;
                }

                if (skipping) {
                    process = false;
                    if (text.endsWith(commentEnd)) {
                        skipping = false;
                    }
                }

                if (process && !text.startsWith(comment) && text.length > 0) {
                    script += text + '\n';
                }
            } else {
                script += text + '\n';
            }

        });
        return script;
    }

    const collection = {};

    return through2.obj(
        function (file, enc, callback) {
            let error = null;
            try {
                if (!options.powerShellModuleName) {
                    throw new Error('gulp-ps-manifest requires "powerShellModuleName" option: PowerShell module name.');
                }

                const path = Path.parse(file.path);
                if (path.ext === '.ps1') {
                    collection[path.base] = file.contents.toString('utf8');
                }
            } catch (e) {
                error = (!e.plugin || (e.plugin !== PLUGIN_NAME)) ?
                    util.extendError(new pluginError({ plugin: PLUGIN_NAME, message: e.message }), e) : e;
            }

            return callback(error);
        },
        function (callback) {
            try {
                const manifestObject = JSON.parse(fs.readFileSync(options.manifest, 'utf8'));
                manifestObject.powerShellModuleName = options.powerShellModuleName;
                const result = [];
                findCommand(manifestObject.entryPoints, result);
                for (const item of result) {
                    let command: string = item.command;
                    if (options.prefixName) {
                        command = command.replace(options.prefixName, '');
                    }
                    const name = command + '.ps1';
                    if (collection[name]) {
                        item.script = convert(command, name, collection[name]);
                        item.module = options.powerShellModuleName;
                    } else {
                        throw new Error('gulp-ps-manifest failed to locate the script: ' + command);
                    }
                }

                const manifestFile = new Vinyl({
                    cwd: './',
                    path: options.manifest,
                    contents: Buffer.from(JSON.stringify(manifestObject, null, 2), 'utf8')
                });

                this.push(manifestFile);
            } catch (e) {
                const error = (!e.plugin || (e.plugin !== PLUGIN_NAME)) ?
                    util.extendError(new pluginError({ plugin: PLUGIN_NAME, message: e.message }), e) : e;
                log.error(error);
            }

            callback();
        }
    );
}

module.exports = gulpPsManifest;
